import React, { useState, useMemo, useEffect } from 'react';
import { Gamepad2, Sparkles, Check, ChevronDown, ChevronUp, Mail, Package, Zap, Shield, Wrench, Palette, Star, X, Info, ChevronLeft, ChevronRight, Play } from '../lib/icons';
import PS5Builder from '../components/PS5Builder';
import SEOHead from '../components/SEOHead';
import { VideoCard, isYouTube } from '../components/VideoCard';
import ImageSlot from '../components/ImageSlot';
import { fetchControllers, toGalleryItem } from '../lib/controllers-client';
import type { ControllerProduct } from '../lib/controllers-client';
import { useEditMode } from '../contexts/EditModeContext';

// ── Gallery controller shape ───────────────────────────────────────────────
interface GalleryController { id: string; name: string; images: string[]; basePrice: number }

// ── Design configurator modal ──────────────────────────────────────────────
interface Design { id: string; name: string; image: string; basePrice: number }

const PRICING = {
  base: 190,
  clickyTriggers: 60,
  mouseClickButtons: 50,
  rumbleDelete: 25,
  backButtons: 80,
  backPaddles: { none: 0, standard: 80, pro: 120 },
} as const;

type PaddleTier = 'none' | 'standard' | 'pro';

const DesignModal = ({ design, onClose }: { design: Design; onClose: () => void }) => {
  const [clickyTriggers, setClickyTriggers] = useState(false);
  const [mouseClickButtons, setMouseClickButtons] = useState(false);
  const [rumbleDelete, setRumbleDelete] = useState(false);
  const [backButtons, setBackButtons] = useState(false);
  const [backPaddles, setBackPaddles] = useState<PaddleTier>('none');
  const [showTip, setShowTip] = useState<string | null>(null);
  const [mobileShowBuild, setMobileShowBuild] = useState(false);

  const total = useMemo(() => {
    let p = design.basePrice;
    if (clickyTriggers) p += PRICING.clickyTriggers;
    if (mouseClickButtons) p += PRICING.mouseClickButtons;
    if (rumbleDelete) p += PRICING.rumbleDelete;
    if (backButtons) p += PRICING.backButtons;
    p += PRICING.backPaddles[backPaddles];
    return p;
  }, [design.basePrice, clickyTriggers, mouseClickButtons, rumbleDelete, backButtons, backPaddles]);

  const selectedUpgrades = [
    clickyTriggers    ? { label: 'Mouse Click Triggers',     price: PRICING.clickyTriggers }    : null,
    mouseClickButtons ? { label: 'Mouse Click Face Buttons', price: PRICING.mouseClickButtons }  : null,
    rumbleDelete      ? { label: 'Rumble Motor Delete',      price: PRICING.rumbleDelete }       : null,
    backButtons       ? { label: 'Back Buttons (up to 4)',   price: PRICING.backButtons }        : null,
    backPaddles !== 'none'
      ? { label: backPaddles === 'standard' ? 'Standard Paddles' : 'Pro Paddles',
          price: PRICING.backPaddles[backPaddles] }
      : null,
  ].filter(Boolean) as { label: string; price: number }[];

  const handleOrder = () => {
    const lines = [
      `Custom PS5 Controller Order`,
      `Design: ${design.name}`,
      `Base (TMR joysticks included): $${design.basePrice}`,
      clickyTriggers    ? `+ Mouse Click Triggers: $${PRICING.clickyTriggers}`             : null,
      mouseClickButtons ? `+ Mouse Click Face Buttons & D-Pad: $${PRICING.mouseClickButtons}` : null,
      rumbleDelete      ? `+ Rumble Motor Delete: $${PRICING.rumbleDelete}`                : null,
      backButtons       ? `+ Back Buttons (up to 4): $${PRICING.backButtons}`              : null,
      backPaddles !== 'none'
        ? `+ Back Paddles (${backPaddles}): $${PRICING.backPaddles[backPaddles]}`
        : null,
      `Total: $${total}`,
    ].filter(Boolean).join('\n');
    window.location.href = `mailto:info@gamefixsolutions.com?subject=${encodeURIComponent('Custom PS5 Controller Order')}&body=${encodeURIComponent(lines + '\n\nPlease send me an invoice.')}`;
  };

  // Shared option row renderer
  const OptionRow = ({
    id, label, sub, price, checked, onChange, tip,
  }: {
    id: string; label: string; sub: string; price: string;
    checked: boolean; onChange: () => void; tip?: string;
  }) => (
    <button
      onClick={onChange}
      className={`w-full text-left px-3 py-3 rounded-xl border-2 transition-all ${checked ? 'border-[#39FF14] bg-[#39FF14]/10' : 'border-gray-700 hover:border-gray-600 bg-gray-900'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="text-[#E0E0E0] font-semibold text-sm leading-tight">{label}</p>
            {tip && (
              <button
                onMouseEnter={() => setShowTip(id)}
                onMouseLeave={() => setShowTip(null)}
                onClick={e => e.stopPropagation()}
                className="relative shrink-0"
              >
                <Info className="h-3 w-3 text-[#00BFFF]" />
                {showTip === id && (
                  <div className="absolute left-0 top-5 w-52 bg-black border border-gray-700 rounded-lg p-2 text-xs text-[#B0B0B0] z-20 shadow-xl">
                    {tip}
                  </div>
                )}
              </button>
            )}
          </div>
          <p className="text-[#B0B0B0] text-xs mt-0.5 leading-tight">{sub}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[#39FF14] font-bold text-sm">{price}</span>
          <div className={`w-4.5 h-4.5 rounded border-2 flex items-center justify-center transition-all ${checked ? 'bg-[#39FF14] border-[#39FF14]' : 'border-gray-600'}`}>
            {checked && <Check className="h-2.5 w-2.5 text-black" />}
          </div>
        </div>
      </div>
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* ── Modal shell ──────────────────────────────────────────────────────── */}
      <div
        className="
          bg-[#0D0D0D] border border-gray-700 rounded-t-2xl md:rounded-2xl
          w-full md:max-w-4xl shadow-2xl
          flex flex-col
          /* Mobile: slides up from bottom, max 92vh, scrollable as one column */
          max-h-[92dvh] md:max-h-[88vh]
          /* Desktop: fixed height, overflow hidden (columns handle own scroll) */
          md:overflow-hidden
        "
        onClick={e => e.stopPropagation()}
      >
        {/* ── Header (always visible) ───────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 shrink-0">
          <h2 className="text-xl font-bold text-[#E0E0E0]">{design.name}</h2>
          <button onClick={onClose} className="text-[#B0B0B0] hover:text-[#E0E0E0] transition-colors p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Body: two columns on desktop, single column on mobile ─────────── */}
        <div className="flex flex-col md:flex-row flex-1 min-h-0">

          {/* ── LEFT COLUMN — sticky summary (desktop) ────────────────────── */}
          <div className="md:w-[42%] shrink-0 flex flex-col border-b md:border-b-0 md:border-r border-gray-800">
            {/* Controller image */}
            <div className="aspect-[4/3] md:aspect-square overflow-hidden bg-gray-900 shrink-0">
              <img
                src={design.image}
                alt={design.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Build summary — visible on desktop, collapsible trigger on mobile */}
            <div className="flex-1 p-4 flex flex-col gap-3">
              {/* Base */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-[#B0B0B0] mb-1">Base Build</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#E0E0E0]">TMR Joysticks</span>
                  <span className="text-sm text-[#39FF14] font-semibold">Included</span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-sm text-[#E0E0E0]">Base controller</span>
                  <span className="text-sm text-[#E0E0E0]">${design.basePrice}</span>
                </div>
              </div>

              {/* Selected upgrades */}
              {selectedUpgrades.length > 0 && (
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-[#B0B0B0] mb-1">Upgrades</p>
                  <div className="space-y-0.5">
                    {selectedUpgrades.map(u => (
                      <div key={u.label} className="flex items-center justify-between">
                        <span className="text-sm text-[#E0E0E0] truncate pr-2">{u.label}</span>
                        <span className="text-sm text-[#39FF14] font-semibold shrink-0">+${u.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Total + CTA — pushed to bottom */}
              <div className="mt-auto pt-3 border-t border-gray-800 space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-bold text-[#E0E0E0] uppercase tracking-wide">Total</span>
                  <span className="text-3xl font-black text-[#39FF14]">${total}</span>
                </div>
                <button
                  onClick={handleOrder}
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF1744] to-[#39FF14] hover:from-[#39FF14] hover:to-[#FF1744] text-white py-3 rounded-lg font-bold transition-all text-sm"
                >
                  <Mail className="h-4 w-4" />
                  Order This Build
                </button>
                <p className="text-[10px] text-[#B0B0B0] text-center">2–4 day turnaround · 90-day warranty</p>
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN — independently scrollable options (desktop) ─── */}
          <div className="flex-1 overflow-y-auto md:overflow-y-auto">
            <div className="p-4 space-y-5">

              {/* PERFORMANCE GROUP */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-[#00BFFF] mb-2">Performance</p>
                <div className="space-y-2">
                  {/* TMR included badge */}
                  <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/30">
                    <div>
                      <p className="text-[#E0E0E0] font-semibold text-sm">TMR Hall-Effect Joysticks</p>
                      <p className="text-[#B0B0B0] text-xs mt-0.5">Anti-drift — permanent fix</p>
                    </div>
                    <span className="text-[#39FF14] font-bold text-sm shrink-0 ml-2">Included</span>
                  </div>

                  <OptionRow
                    id="triggers" label="Mouse Click Triggers" sub="Hair triggers + bumpers — 70% less travel"
                    price={`+$${PRICING.clickyTriggers}`} checked={clickyTriggers} onChange={() => setClickyTriggers(p => !p)}
                    tip="70% less trigger travel. Instant actuation for FPS and competitive play."
                  />
                  <OptionRow
                    id="buttons" label="Mouse Click Face Buttons" sub="All face buttons (X, O, □, △) + D-Pad"
                    price={`+$${PRICING.mouseClickButtons}`} checked={mouseClickButtons} onChange={() => setMouseClickButtons(p => !p)}
                    tip="All face buttons + D-Pad fitted with mouse-click switches. Precise, tactile, satisfying."
                  />
                  <OptionRow
                    id="rumble" label="Rumble Motor Delete" sub="Lighter build, zero vibration"
                    price={`+$${PRICING.rumbleDelete}`} checked={rumbleDelete} onChange={() => setRumbleDelete(p => !p)}
                    tip="Removes both rumble motors for a lighter, more responsive controller. Popular with competitive players."
                  />
                </div>
              </div>

              {/* REAR CONTROLS GROUP */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-[#FF9800] mb-2">Rear Controls</p>
                <OptionRow
                  id="backbtns" label="Back Buttons (up to 4)" sub="Permanently installed, choose pressure & pairing at build time"
                  price={`+$${PRICING.backButtons}`} checked={backButtons} onChange={() => setBackButtons(p => !p)}
                  tip="Up to 4 buttons paired to a face button at installation. Choose pressure (100g / 160g / 200g) and size — pairing is fixed at build time."
                />
              </div>

              {/* BACK PADDLES GROUP */}
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-[#FF9800] mb-2">Back Paddles</p>
                <div className="space-y-2">
                  {([
                    { id: 'none',     label: 'No Paddles',       price: 'Included',                         sub: '' },
                    { id: 'standard', label: 'Standard Paddles', price: `+$${PRICING.backPaddles.standard}`, sub: '4 programmable paddles' },
                    { id: 'pro',      label: 'Pro Paddles',       price: `+$${PRICING.backPaddles.pro}`,      sub: 'OLED · profiles · adj. triggers', badge: 'PREMIUM' },
                  ] as const).map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setBackPaddles(opt.id)}
                      className={`w-full text-left px-3 py-3 rounded-xl border-2 transition-all ${backPaddles === opt.id ? 'border-[#39FF14] bg-[#39FF14]/10' : 'border-gray-700 hover:border-gray-600 bg-gray-900'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[#E0E0E0] font-semibold text-sm">{opt.label}</span>
                            {'badge' in opt && opt.badge && (
                              <span className="text-[10px] bg-gradient-to-r from-[#FF1744] to-[#39FF14] text-white px-1.5 py-0.5 rounded font-bold">
                                {opt.badge}
                              </span>
                            )}
                          </div>
                          {opt.sub && <p className="text-[#B0B0B0] text-xs mt-0.5">{opt.sub}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`font-bold text-sm ${opt.price === 'Included' ? 'text-[#B0B0B0]' : 'text-[#39FF14]'}`}>{opt.price}</span>
                          {backPaddles === opt.id && <Check className="h-4 w-4 text-[#39FF14]" />}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Mobile sticky bottom bar ──────────────────────────────────────── */}
        <div className="md:hidden shrink-0 border-t border-gray-800 bg-[#0D0D0D]">
          {/* Expandable build summary */}
          {mobileShowBuild && selectedUpgrades.length > 0 && (
            <div className="px-4 pt-3 pb-1 space-y-1 border-b border-gray-800">
              <div className="flex justify-between text-xs text-[#B0B0B0]">
                <span>TMR Joysticks</span><span className="text-[#39FF14]">Included</span>
              </div>
              <div className="flex justify-between text-xs text-[#B0B0B0]">
                <span>Base controller</span><span>${design.basePrice}</span>
              </div>
              {selectedUpgrades.map(u => (
                <div key={u.label} className="flex justify-between text-xs text-[#B0B0B0]">
                  <span className="truncate pr-2">{u.label}</span>
                  <span className="text-[#39FF14] shrink-0">+${u.price}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => setMobileShowBuild(p => !p)}
              className="flex-1 text-left"
            >
              <p className="text-[10px] text-[#B0B0B0] uppercase tracking-wide">Total</p>
              <p className="text-2xl font-black text-[#39FF14]">${total}</p>
            </button>
            <button
              onClick={handleOrder}
              className="flex items-center gap-2 bg-gradient-to-r from-[#FF1744] to-[#39FF14] hover:from-[#39FF14] hover:to-[#FF1744] text-white px-5 py-3 rounded-lg font-bold text-sm transition-all shrink-0"
            >
              <Mail className="h-4 w-4" />
              Order Build
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Fallbacks used if Supabase fetch fails or is still loading
const FALLBACK_CUSTOM: GalleryController[] = [
  { id: 'custom7',  name: 'Custom Build 7',  images: ['/custom7.jpg'],  basePrice: 190 },
  { id: 'custom8',  name: 'Custom Build 8',  images: ['/custom8.jpg'],  basePrice: 190 },
  { id: 'custom10', name: 'Custom Build 10', images: ['/custom10.jpg'], basePrice: 190 },
  { id: 'custom12', name: 'Custom Build 12', images: ['/custom12.jpg'], basePrice: 190 },
  { id: 'custom13', name: 'Custom Build 13', images: ['/custom13.jpg'], basePrice: 190 },
  { id: 'custom14', name: 'Custom Build 14', images: ['/custom14.jpg'], basePrice: 190 },
  { id: 'joker',            name: 'Joker',             images: ['/joker.JPEG'],                                    basePrice: 190 },
  { id: 'astroburger',     name: 'Astroburger',       images: ['/dualsensecustomgraphic-astroburger.jpg'],        basePrice: 190 },
  { id: 'bear',            name: 'Bear',              images: ['/dualsensecustomgraphic-bear.jpg'],               basePrice: 190 },
  { id: 'cottoncandy',     name: 'Cotton Candy',      images: ['/dualsensecustomgraphic-cottoncandy.jpg'],        basePrice: 190 },
  { id: 'digitalvenom',    name: 'Digital Venom',     images: ['/dualsensecustomgraphic-digitalvenom.jpg'],       basePrice: 190 },
  { id: 'fireandice',      name: 'Fire & Ice',        images: ['/dualsensecustomgraphic-fire&ice.jpg'],           basePrice: 190 },
  { id: 'golddragon',      name: 'Gold Dragon',       images: ['/dualsensecustomgraphic-golddragon.jpg'],         basePrice: 190 },
  { id: 'lightning',       name: 'Lightning',         images: ['/dualsensecustomgraphic-lightning.jpg'],          basePrice: 190 },
  { id: 'milesmorales',    name: 'Miles Morales',     images: ['/dualsensecustomgraphic-milesmorales.jpg'],       basePrice: 190 },
  { id: 'patriot',         name: 'Patriot',           images: ['/dualsensecustomgraphic-patriot.jpg'],            basePrice: 190 },
  { id: 'pearlwhite',      name: 'Pearl White',       images: ['/dualsensecustomgraphic-pearlwhite.jpg'],         basePrice: 190 },
  { id: 'purplehoneycomb', name: 'Purple Honeycomb',  images: ['/dualsensecustomgraphic-purplehoneycomb.jpg'],    basePrice: 190 },
  { id: 'skull',           name: 'Skull',             images: ['/dualsensecustomgraphic-skull.jpg'],              basePrice: 190 },
  { id: 'spider',          name: 'Spider',            images: ['/dualsensecustomgraphic-spider.jpg'],             basePrice: 190 },
  { id: 'vangogh',         name: 'Van Gogh',          images: ['/dualsensecustomgraphic-vangogh.jpg'],            basePrice: 190 },
  { id: 'whitedragon',     name: 'White Dragon',      images: ['/dualsensecustomgraphic-whitedragon.jpg'],        basePrice: 190 },
];
const FALLBACK_STANDARD: GalleryController[] = [
  { id: 'blush-pink',    name: 'Blush Pink',             images: ['/IMG_3721.JPEG'],               basePrice: 190 },
  { id: 'lakers',        name: 'Lakers Edition',          images: ['/IMG_3734.JPEG'],               basePrice: 175 },
  { id: 'purple-carbon', name: 'Purple Carbon',           images: ['/img_3347.jpeg'],               basePrice: 175 },
  { id: 'iron-man',      name: 'Iron Man Edition',        images: ['/img_3358.jpeg'],               basePrice: 175 },
  { id: 'gold-prestige', name: 'Gold Prestige',           images: ['/img_3368.jpeg'],               basePrice: 175 },
  { id: 'blue-phantom',  name: 'Blue Phantom',            images: ['/img_3438.jpeg'],               basePrice: 175 },
  { id: 'super-station', name: 'Super Station',           images: ['/img_3330.jpeg'],               basePrice: 175 },
  { id: 'urban-camo',    name: 'Urban Camo',              images: ['/img_3481.jpeg'],               basePrice: 175 },
  { id: 'arctic-silver', name: 'Arctic Silver',           images: ['/img_3363.jpeg'],               basePrice: 175 },
  { id: 'chrome-pink',   name: 'Chrome Pink',             images: ['/img_7067.jpg'],                basePrice: 175 },
  { id: 'camo',          name: 'Camo',                    images: ['/cammo.JPEG'],                  basePrice: 175 },
  { id: 'forest-green',  name: 'Forest Green',            images: ['/forest_green_.JPEG'],          basePrice: 175 },
  { id: 'yellow',        name: 'Yellow',                  images: ['/yellow.JPEG'],                 basePrice: 175 },
  { id: 'wood-grain',    name: 'Wood Grain',              images: ['/wood_grain.JPEG'],             basePrice: 175 },
  { id: 'matte-red',     name: 'Matte Red Gold Trim',     images: ['/matte_red,_gold_trim.JPEG'],   basePrice: 175 },
  { id: 'retro-30th',    name: 'Retro 30th Anniversary',  images: ['/retro_30th_aniversary_.JPEG'], basePrice: 190 },
  { id: 'retro-station', name: 'Retro Super Station',     images: ['/retro_super_station.jpeg'],    basePrice: 175 },
];
const FALLBACK_EDGE: GalleryController[] = [
  { id: 'dse-pink',         name: 'Pink',              images: ['/DSe-pink.jpg'],          basePrice: 190 },
  { id: 'dse-red',          name: 'Red',               images: ['/DSe-red.jpg'],           basePrice: 190 },
  { id: 'dse-white',        name: 'White',             images: ['/DSe-white.jpg'],         basePrice: 190 },
  { id: 'dse-30th',         name: '30th Anniversary',  images: ['/DSe-30thaniversary.jpg'], basePrice: 190 },
];

// Product showcase videos — drop file paths into `src` when ready
const VIDEO_UPGRADES = [
  { id: 'tmr',          title: 'TMR Joysticks',           sub: 'Anti-drift, forever',             src: 'https://youtube.com/shorts/LSeE6rPuPfI' },
  { id: 'clicky',       title: 'Clicky Triggers & Buttons', sub: 'Hear and feel every input',     src: 'https://youtube.com/shorts/DG_NYcOTMN4' },
  { id: 'back-buttons', title: 'Back Buttons',             sub: 'Never lift your thumbs',          src: null as string | null },
  { id: 'rise4',        title: 'Rise4 Paddles',            sub: 'Remap without leaving sticks',    src: 'https://youtube.com/shorts/oPhZAAoPmFQ' },
  { id: 'rise4-max',    title: 'Rise4 +Max',               sub: 'More reach, same control',        src: 'https://youtube.com/shorts/lQ8W8y_539Q' },
  { id: 'spark',        title: 'Spark Paddles',            sub: 'Slim profile, maximum feel',      src: null as string | null },
];

// Educational guide videos
const VIDEO_GUIDES = [
  { id: 'analog-vs-hall-vs-tmr',   title: 'Analog vs Hall vs TMR',       sub: "What's the difference?",         src: 'https://youtube.com/shorts/GFXFFoQktQI' },
  { id: 'rise4-vs-rise4max-spark', title: 'Rise4 vs Rise4+Max vs Spark', sub: 'Which paddle should you pick?',  src: null as string | null },
  { id: 'paddles-vs-buttons',      title: 'Paddles vs Back Buttons',      sub: 'Which is right for you?',        src: null as string | null },
  { id: 'rise4-setup',             title: 'Rise4 Setup Guide',            sub: 'Remap in 60 seconds',            src: null as string | null },
  { id: 'ksilver-vs-gulikit',      title: 'K Silver vs GuliKit',          sub: 'Joystick comparison',            src: null as string | null },
  { id: 'stick-drift-cause',       title: 'What Causes Stick Drift?',     sub: 'And how we fix it for good',     src: null as string | null },
];


const PERFORMANCE_UPGRADES = [
  {
    id: 'ksilver-tmr',
    slotId: 'performance-ksilver-tmr',
    fallbackSrc: '/controllerupgrades-ksilverjs13pro.jpg' as string | null,
    category: 'Joystick',
    categoryColor: '#39FF14',
    name: 'K Silver TMR',
    tagline: 'Hall-effect precision, drift-free',
    description: 'Magnetic TMR sensors that physically cannot wear out. A cost-effective, permanent fix for stick drift.',
  },
  {
    id: 'gulikit-tmr',
    slotId: 'performance-gulikit-tmr',
    fallbackSrc: '/controllerupgrades-gulikittmr.jpg' as string | null,
    category: 'Joystick',
    categoryColor: '#39FF14',
    name: 'GuliKit TMR',
    tagline: 'Premium TMR with adjustable tension',
    description: 'Premium hall-effect joysticks. The adjustable-tension version lets you swap stick heights and resistance on the fly.',
  },
  {
    id: 'mouse-click-triggers',
    slotId: 'performance-triggers',
    fallbackSrc: '/clicky-triggers-buttons.mp4' as string | null,
    category: 'Triggers',
    categoryColor: '#00BFFF',
    name: 'Mouse Click Triggers',
    tagline: '70% less travel — hear every input',
    description: 'Hair-trigger switches replace the standard trigger mechanism. Instant actuation with a satisfying tactile click.',
  },
  {
    id: 'mouse-click-buttons',
    slotId: 'performance-buttons',
    fallbackSrc: '/img_8233.jpg' as string | null,
    category: 'Buttons',
    categoryColor: '#00BFFF',
    name: 'Mouse Click Buttons',
    tagline: 'Mechanical feel on every press',
    description: 'All face buttons (X, O, □, △) + D-Pad fitted with mouse-click switches. Precise, tactile, and satisfying.',
  },
  {
    id: 'rise4',
    slotId: 'performance-rise4',
    fallbackSrc: '/rise4 back paddles.mp4' as string | null,
    category: 'Back Paddles',
    categoryColor: '#FF9800',
    name: 'Rise4',
    tagline: 'Remap without lifting your thumbs',
    description: '4 programmable back paddles. Map any button, change on the fly — no tools required.',
  },
  {
    id: 'rise4-max',
    slotId: 'performance-rise4-max',
    fallbackSrc: '/rise4+max-adapativetriggers.mp4' as string | null,
    category: 'Back Paddles',
    categoryColor: '#FF9800',
    name: 'Rise4 +Max',
    tagline: 'Extended reach for bigger hands',
    description: 'All the benefits of Rise4 with longer paddles for a more natural reach during extended sessions.',
  },
  {
    id: 'spark',
    slotId: 'performance-spark',
    fallbackSrc: '/img_8232.jpg' as string | null,
    category: 'Back Paddles',
    categoryColor: '#FF9800',
    name: 'Spark',
    tagline: 'Slim profile, zero compromise',
    description: 'Low-profile paddle design that stays out of your way. Remappable buttons without adding bulk.',
  },
  {
    id: 'rear-action-buttons',
    slotId: 'performance-rear-action-buttons',
    fallbackSrc: '/ps5ds-controllerupgrade-actionbuttons2.jpg' as string | null,
    category: 'Rear Buttons',
    categoryColor: '#FF1744',
    name: 'Rear Action Buttons',
    tagline: 'Up to 4 permanently installed buttons',
    description: 'Buttons soldered directly to the back of the controller. Choose pressure (100g / 160g / 200g) and face-button pairing at build time.',
  },
  {
    id: 'transparent-shell',
    slotId: 'performance-transparent-shell',
    fallbackSrc: null as string | null,
    category: 'Cosmetic',
    categoryColor: '#E91E63',
    name: 'Transparent Shell',
    tagline: 'See through — show your internals',
    description: 'Clear front and rear shells that let the internals and LED lighting show through. Available for DualSense and DualSense Edge.',
  },
  {
    id: 'grip-rear-shell',
    slotId: 'performance-grip-rear-shell',
    fallbackSrc: null as string | null,
    category: 'Cosmetic',
    categoryColor: '#E91E63',
    name: 'Grip Rear Shell',
    tagline: 'Textured grip for extended sessions',
    description: 'Soft-touch matte rear shell with enhanced grip texture. Reduces controller fatigue during long sessions.',
  },
  {
    id: 'led-light-kit-dualsense',
    slotId: 'performance-led-kit-dualsense',
    fallbackSrc: null as string | null,
    category: 'Cosmetic',
    categoryColor: '#E91E63',
    name: 'PS5 DualSense LED Light Kit',
    tagline: 'Glow-through lighting on every input',
    description: 'Illuminated face buttons, D-pad, and accent lighting visible through the shell vents and seams. Best paired with a transparent shell for maximum effect.',
  },
  {
    id: 'led-light-kit-edge',
    slotId: 'performance-led-kit-edge',
    fallbackSrc: null as string | null,
    category: 'Cosmetic',
    categoryColor: '#E91E63',
    name: 'PS5 Edge LED Light Kit',
    tagline: 'Glow-through lighting on every input',
    description: 'Illuminated face buttons, D-pad, and accent lighting visible through the shell vents and seams on the PS5 Edge controller. Best paired with a transparent shell for maximum effect.',
  },
];

// VideoCard, isYouTube, getYouTubeEmbedUrl imported from ../components/VideoCard

// ── Lightbox carousel modal ────────────────────────────────────────────────
const LightboxModal = ({
  controller,
  onClose,
  onConfigure,
}: {
  controller: GalleryController;
  onClose: () => void;
  onConfigure: (design: Design) => void;
}) => {
  const [idx, setIdx] = useState(0);
  const multi = controller.images.length > 1;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && multi) setIdx(i => (i - 1 + controller.images.length) % controller.images.length);
      if (e.key === 'ArrowRight' && multi) setIdx(i => (i + 1) % controller.images.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [controller.images.length, multi, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/92 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="relative w-full max-w-3xl" onClick={e => e.stopPropagation()}>
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-11 right-0 text-[#B0B0B0] hover:text-white transition-colors"
        >
          <X className="h-7 w-7" />
        </button>

        {/* Image */}
        <div className="relative aspect-square md:aspect-[4/3] rounded-2xl overflow-hidden bg-gray-900 border border-gray-800 shadow-2xl">
          <img
            src={controller.images[idx]}
            alt={`${controller.name} — angle ${idx + 1}`}
            className="w-full h-full object-cover"
          />

          {multi && (
            <>
              <button
                onClick={() => setIdx(i => (i - 1 + controller.images.length) % controller.images.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full backdrop-blur-sm transition-all hover:scale-105"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIdx(i => (i + 1) % controller.images.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full backdrop-blur-sm transition-all hover:scale-105"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 text-xs text-white">
                {idx + 1} / {controller.images.length}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-5 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-[#E0E0E0] font-bold text-xl">{controller.name}</h3>
            {multi && (
              <div className="flex gap-1.5 mt-2">
                {controller.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-[#39FF14]' : 'w-1.5 bg-gray-600 hover:bg-gray-500'}`}
                  />
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => onConfigure({ id: controller.id, name: controller.name, image: controller.images[0], basePrice: controller.basePrice })}
            className="shrink-0 flex items-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black px-6 py-3 rounded-lg font-bold text-sm transition-all"
          >
            Configure & Price →
          </button>
        </div>
      </div>
    </div>
  );
};

const CustomControllersPage = () => {
  const editMode = useEditMode();
  const [selectedDesign, setSelectedDesign] = useState<Design | null>(null);
  const [lightboxController, setLightboxController] = useState<GalleryController | null>(null);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);

  // Live catalog from Supabase; falls back to hardcoded arrays on error
  const [liveCustom,   setLiveCustom]   = useState<GalleryController[] | null>(null);
  const [liveStandard, setLiveStandard] = useState<GalleryController[] | null>(null);
  const [liveEdge,     setLiveEdge]     = useState<GalleryController[] | null>(null);

  useEffect(() => {
    fetchControllers().then((products: ControllerProduct[]) => {
      const byType = (t: string) => products
        .filter(p => p.gallery_type === t)
        .map(toGalleryItem)
        .filter(g => g.images.length > 0);
      setLiveCustom(byType('custom'));
      setLiveStandard(byType('standard'));
      setLiveEdge(byType('edge'));
    }).catch(() => {/* use fallbacks */});
  }, []);

  const customGallery   = liveCustom   ?? FALLBACK_CUSTOM;
  const standardGallery = liveStandard ?? FALLBACK_STANDARD;
  const edgeGallery     = liveEdge     ?? FALLBACK_EDGE;

  const openLightbox = (controller: GalleryController) => setLightboxController(controller);
  const handleConfigureFromLightbox = (design: Design) => {
    setLightboxController(null);
    setSelectedDesign(design);
  };
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [isOtherControllersExpanded, setIsOtherControllersExpanded] = useState(false);
  const [galleryTab, setGalleryTab] = useState<'custom' | 'standard' | 'edge' | 'performance'>('custom');
  const [builderTab, setBuilderTab] = useState<'dualsense' | 'edge'>('dualsense');

  // Sequential video playback
  const mp4Videos = useMemo(
    () => [...VIDEO_UPGRADES, ...VIDEO_GUIDES].filter(v => v.src && !isYouTube(v.src!)),
    [],
  );
  const [activeVideoId, setActiveVideoId] = useState<string | null>(mp4Videos[0]?.id ?? null);
  const [isVideoMuted, setIsVideoMuted] = useState(true);

  const advanceVideo = (currentId: string) => {
    const idx = mp4Videos.findIndex(v => v.id === currentId);
    const next = mp4Videos[(idx + 1) % mp4Videos.length];
    if (next) setActiveVideoId(next.id);
  };

  const tiers = [
    {
      id: 'standard',
      name: 'STANDARD',
      icon: Gamepad2,
      price: 'Starting at $175',
      benefit: 'Drift-proof performance',
      features: [
        'OEM PS5 controller',
        'TMR hall-effect joysticks (anti-drift) - standard',
        '90-day workmanship warranty'
      ],
      color: '#00BFFF',
      bgGlow: 'from-[#00BFFF]/20 to-transparent'
    },
    {
      id: 'plus',
      name: 'PLUS',
      icon: Zap,
      price: 'Starting at $195',
      benefit: 'Lightning-fast response',
      features: [
        'Everything in Standard',
        'Hair-click triggers & bumpers',
        'Reduced trigger travel distance',
        'ASMR-quality tactile feedback'
      ],
      color: '#39FF14',
      bgGlow: 'from-[#39FF14]/20 to-transparent',
      popular: true
    },
    {
      id: 'advanced',
      name: 'ADVANCED',
      icon: Star,
      price: 'Starting at $215',
      benefit: 'Tournament-grade precision',
      features: [
        'Everything in Plus',
        'Clicky face buttons',
        'Clicky D-pad',
        'Enhanced tactile feedback throughout'
      ],
      color: '#FF1744',
      bgGlow: 'from-[#FF1744]/20 to-transparent'
    },
    {
      id: 'pro',
      name: 'PRO',
      icon: Shield,
      price: 'Starting at $295',
      benefit: 'Ultimate competitive edge',
      features: [
        'Everything in Advanced',
        'Back paddles (professional remap kit)',
        'Fully programmable buttons',
        'Elite-level customization'
      ],
      color: '#B8860B',
      bgGlow: 'from-[#B8860B]/20 to-transparent'
    }
  ];

  const upgradeFeatures = [
    {
      title: 'GuliKit Hall-Effect Joysticks',
      description: 'Magnetic sensor technology eliminates stick drift permanently. No more dead zones or calibration issues.',
      video: null, // placeholder for future video
      icon: Gamepad2,
      color: '#00BFFF'
    },
    {
      title: 'Hair-Click Triggers & Bumpers',
      description: 'Ultra-short trigger travel with satisfying tactile click. Faster response times for competitive advantage.',
      video: null,
      icon: Zap,
      color: '#39FF14'
    },
    {
      title: 'Clicky Buttons & D-Pad',
      description: 'Mechanical switch feel with instant actuation. Every input is precise and satisfying.',
      video: null,
      icon: Star,
      color: '#FF1744'
    },
    {
      title: 'Back Paddle Systems',
      description: 'Professional remap kit allows button mapping without taking thumbs off sticks. Tournament-approved.',
      video: null,
      icon: Shield,
      color: '#B8860B'
    },
    {
      title: 'Custom Shells & Buttons',
      description: 'Premium aftermarket shells, transparent options, soft-touch finishes, and custom button colors.',
      video: null,
      icon: Palette,
      color: '#E91E63'
    },
    {
      title: 'Rumble Motor Delete',
      description: 'Both vibration motors removed for a lighter, snappier feel. Popular with competitive FPS and fighting game players who want zero input lag.',
      video: null,
      icon: Zap,
      color: '#FF9800'
    },
    {
      title: 'Back Buttons (Up to 4)',
      description: 'Up to 4 buttons permanently installed on the back of the controller. You choose the button pairing, click pressure (100g / 160g / 200g), and size at the time of the build — the pairing is fixed at installation.',
      video: null,
      icon: Shield,
      color: '#9C27B0'
    }
  ];

  const faqs = [
    {
      question: 'What are GuliKit joysticks?',
      answer: 'GuliKit joysticks use TMR (Tunneling Magneto-Resistance) hall-effect sensors instead of traditional potentiometers. This magnetic sensing technology eliminates physical contact wear, which is the root cause of stick drift. They provide a lifetime of precise, drift-free control.'
    },
    {
      question: 'Do these prevent stick drift?',
      answer: 'Yes, 100%. Hall-effect joysticks use magnetic sensors that never wear out from physical contact. Traditional joysticks use potentiometers that degrade over time, causing drift. With GuliKit hall-effect sticks, drift is physically impossible.'
    },
    {
      question: 'Can I send in my controller?',
      answer: 'Absolutely! We offer mail-in services for joystick upgrades and full custom builds. Choose standard (5-7 business days) or expedited (1-2 business days) turnaround. Ship your controller to us and we\'ll get it back to you fast.'
    },
    {
      question: 'What warranty is included?',
      answer: 'All custom builds include a 90-day warranty covering workmanship and installed components. New OEM controllers maintain their original manufacturer warranty. GuliKit joysticks have a 1-year manufacturer warranty.'
    },
    {
      question: 'How long does a build take?',
      answer: 'Standard builds: 5-7 business days. Expedited builds: 1-2 business days. Complex full-custom builds with multiple modifications may take 7-10 business days. We provide regular updates throughout the build process.'
    },
    {
      question: 'Can I request specific colors?',
      answer: 'Yes! We offer both curated standard designs and fully custom color combinations. For custom builds, you can specify shell colors, button colors, trim accents, and finish options. We confirm your exact design before assembly begins.'
    }
  ];

  return (
    <div className="bg-[#0D0D0D] min-h-screen">
      <SEOHead
        title="Custom PS5 Controllers | TMR Joysticks, Mouse Click Triggers, Back Paddles | GameFix Solutions"
        description="Built-to-order custom PS5 DualSense controllers with TMR hall-effect joysticks, mouse click triggers, clicky buttons, back paddles, and custom shells. Built in-house in San Diego. Mail-in available nationwide."
        keywords="custom ps5 controller, custom dualsense, ps5 tmr joystick, ps5 mouse click triggers, ps5 back paddles, ps5 hall effect controller, custom ps5 build"
        canonical="https://gamefixsolutions.com/custom-controllers"
        jsonLD={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'Custom PS5 DualSense Controller',
          description: 'Custom PS5 controller builds with TMR hall-effect joysticks, mouse click triggers, back paddles, and cosmetic customization.',
          brand: { '@type': 'Brand', name: 'GameFix Solutions' },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            price: '190',
            priceValidUntil: '2027-01-01',
            availability: 'https://schema.org/InStock',
          },
        }}
      />
      {lightboxController && (
        <LightboxModal
          controller={lightboxController}
          onClose={() => setLightboxController(null)}
          onConfigure={handleConfigureFromLightbox}
        />
      )}
      {selectedDesign && <DesignModal design={selectedDesign} onClose={() => setSelectedDesign(null)} />}
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-black via-[#0D0D0D] to-[#0D0D0D] py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMzlGRjE0IiBzdHJva2Utd2lkdGg9IjAuNSIgb3BhY2l0eT0iMC4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-6 py-2 mb-6">
              <Sparkles className="h-5 w-5 text-[#39FF14]" />
              <span className="text-[#39FF14] font-semibold">PREMIUM SERVICE</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-[#E0E0E0] mb-6">
              Custom Performance Controllers — Built Your Way
            </h1>

            <p className="text-xl md:text-2xl text-[#B0B0B0] max-w-4xl mx-auto mb-12">
              Drift-proof joysticks, hair-click triggers, tactile buttons, back paddles, and custom shells. Built in-house using OEM controllers.
            </p>

          </div>
        </div>
      </section>

      {/* Controller Tiers Section */}
      <section id="tiers" className="py-20 bg-gradient-to-b from-[#0D0D0D] to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">
              Choose Your Performance Tier
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Each tier builds on the last, adding professional features for competitive gamers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier) => {
              const Icon = tier.icon;
              return (
                <div
                  key={tier.id}
                  className={`relative bg-gray-900 rounded-2xl p-6 border-2 transition-all duration-300 cursor-pointer ${
                    selectedTier === tier.id
                      ? 'border-[#39FF14] shadow-lg shadow-[#39FF14]/20 scale-105'
                      : 'border-gray-800 hover:border-gray-700 hover:scale-102'
                  }`}
                  onClick={() => setSelectedTier(tier.id)}
                >
                  {tier.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#39FF14] text-black px-4 py-1 rounded-full text-sm font-bold">
                      MOST POPULAR
                    </div>
                  )}

                  <div className={`bg-gradient-to-br ${tier.bgGlow} p-4 rounded-xl mb-4`}>
                    <Icon className="h-10 w-10 mx-auto" style={{ color: tier.color }} />
                  </div>

                  <h3 className="text-2xl font-bold text-[#E0E0E0] mb-2 text-center">
                    {tier.name}
                  </h3>

                  <p className="text-sm text-[#B0B0B0] text-center mb-4 italic">
                    {tier.benefit}
                  </p>

                  <p className="text-xl font-bold text-center mb-6" style={{ color: tier.color }}>
                    {tier.price}
                  </p>

                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm text-[#B0B0B0]">
                        <Check className="h-4 w-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 ${
                      selectedTier === tier.id
                        ? 'bg-[#39FF14] text-black'
                        : 'bg-gray-800 text-[#E0E0E0] hover:bg-gray-700'
                    }`}
                  >
                    Select {tier.name}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Optional Upgrade Box */}
          <div className="mt-12 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 border-2 border-[#FFD700] shadow-lg">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center space-x-4">
                <div className="bg-[#FFD700]/10 p-4 rounded-xl">
                  <Gamepad2 className="h-8 w-8 text-[#FFD700]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#E0E0E0] mb-2">
                    Optional Upgrade: GuliKit Adjustable Tension Joysticks
                  </h3>
                  <p className="text-[#B0B0B0]">
                    Customize your joystick resistance and swap stick heights on the fly. Available for any tier.
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                <div className="bg-[#FFD700] text-black px-6 py-3 rounded-lg font-bold text-xl">
                  +$20
                </div>
              </div>
            </div>
          </div>

          {selectedTier && (
            <div className="mt-12 text-center">
              <a
                href="/#contact"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF1744] to-[#39FF14] hover:from-[#39FF14] hover:to-[#FF1744] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Mail className="h-5 w-5" />
                <span>Continue to Design Selection</span>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Gallery + Upgrade Features */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">Our Builds & Upgrades</h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Every controller is built in-house — see our work, then explore every performance upgrade we offer
            </p>
          </div>

          {/* Gallery tabs */}
          <div className="flex justify-center mb-6">
            <div className="flex bg-gray-900 border border-gray-800 rounded-xl p-1 gap-1">
              {([
                { key: 'custom',      label: 'Custom Graphics',      color: '#39FF14' },
                { key: 'standard',    label: 'Standard Colors',       color: '#00BFFF' },
                { key: 'edge',        label: 'DualSense Edge',        color: '#FF1744' },
                { key: 'performance', label: 'Performance Upgrades',  color: '#FF9800' },
              ] as const).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setGalleryTab(tab.key)}
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
                  style={
                    galleryTab === tab.key
                      ? { backgroundColor: `${tab.color}20`, color: tab.color, border: `1px solid ${tab.color}40` }
                      : { color: '#B0B0B0', border: '1px solid transparent' }
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Grid — controller gallery or performance upgrades */}
          {galleryTab === 'performance' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
              {PERFORMANCE_UPGRADES.map(upgrade => (
                <div
                  key={upgrade.id}
                  className="bg-gray-900 rounded-2xl border border-gray-800 hover:border-gray-600 transition-all duration-300 overflow-hidden flex flex-col"
                >
                  {/* Photo / video / placeholder */}
                  <div className="aspect-video bg-gray-950 overflow-hidden">
                    <ImageSlot
                      slotId={upgrade.slotId}
                      fallbackSrc={upgrade.fallbackSrc ?? ''}
                      fallbackAlt={upgrade.name}
                      className="object-cover"
                      wrapperClassName="w-full h-full"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-5 flex flex-col flex-1">
                    <span
                      className="text-[10px] font-black uppercase tracking-widest mb-2"
                      style={{ color: upgrade.categoryColor }}
                    >
                      {upgrade.category}
                    </span>
                    <h3 className="text-base font-bold text-[#E0E0E0] mb-1">{upgrade.name}</h3>
                    <p className="text-sm font-semibold mb-3" style={{ color: upgrade.categoryColor }}>
                      {upgrade.tagline}
                    </p>
                    <p className="text-xs text-gray-500 leading-relaxed flex-1">{upgrade.description}</p>
                    <a
                      href="/book-repair"
                      className="mt-4 text-xs font-bold text-[#39FF14] hover:underline flex items-center gap-1"
                    >
                      Get a Quote <ChevronRight className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-8">
                {(galleryTab === 'custom' ? customGallery : galleryTab === 'standard' ? standardGallery : edgeGallery).map((controller) => (
                  <div
                    key={controller.id}
                    className="aspect-square rounded-xl overflow-hidden border border-gray-800 hover:border-[#39FF14]/60 transition-all group relative"
                  >
                    <ImageSlot
                      slotId={`ctrl-${galleryTab}-${controller.id}`}
                      fallbackSrc={controller.images[0]}
                      fallbackAlt={controller.name}
                      className="object-cover group-hover:scale-105 transition-transform duration-500"
                      wrapperClassName="w-full h-full"
                    />
                    <div
                      onClick={() => !editMode.active && openLightbox(controller)}
                      className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex flex-col items-end justify-end gap-1 p-2 cursor-pointer z-[5]"
                    >
                      <span className="text-white text-xs font-bold drop-shadow text-center leading-tight bg-black/60 rounded px-1">{controller.name}</span>
                    </div>
                    {editMode.active && editMode.onEditController && (
                      <button
                        onClick={e => { e.stopPropagation(); editMode.onEditController!(controller.id); }}
                        className="absolute top-1.5 left-1.5 z-10 bg-[#39FF14] hover:bg-[#00BFFF] text-black text-[10px] font-black px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Edit product
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="text-center mb-16">
                <p className="text-[#B0B0B0] text-sm mb-3">Want something completely unique?</p>
                <a
                  href={`mailto:info@gamefixsolutions.com?subject=${encodeURIComponent('Custom PS5 Controller Design Request')}&body=${encodeURIComponent('Hi, I have a custom controller design in mind:\n\n')}`}
                  className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] px-6 py-3 rounded-lg font-semibold text-sm transition-all border border-gray-700 hover:border-[#39FF14]"
                >
                  <Mail className="h-4 w-4" /> Submit a Custom Design Request
                </a>
              </div>
            </>
          )}

          {/* Upgrade Features */}
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-[#E0E0E0] mb-2">Performance Upgrades</h3>
            <p className="text-[#B0B0B0]">Mix and match — every upgrade can be added to any build</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {upgradeFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-gray-600 transition-all duration-300">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: `${feature.color}20` }}>
                      <Icon className="h-5 w-5" style={{ color: feature.color }} />
                    </div>
                    <h3 className="text-base font-bold text-[#E0E0E0]">{feature.title}</h3>
                  </div>
                  <p className="text-sm text-[#B0B0B0] leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PS5 Builder Section */}
      <section className="py-8 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">Build & Price Your Controller</h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto mb-8">
              Select your controller model, then mix and match upgrades to get an instant price estimate
            </p>
            {/* Controller type picker */}
            <div className="flex justify-center mb-2">
              <div className="flex bg-gray-900 border border-gray-800 rounded-xl p-1 gap-1">
                {([
                  { key: 'dualsense', label: 'DualSense',       color: '#39FF14' },
                  { key: 'edge',      label: 'DualSense Edge',  color: '#FF1744' },
                ] as const).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setBuilderTab(tab.key)}
                    className="px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-200"
                    style={
                      builderTab === tab.key
                        ? { backgroundColor: `${tab.color}20`, color: tab.color, border: `1px solid ${tab.color}40` }
                        : { color: '#B0B0B0', border: '1px solid transparent' }
                    }
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {builderTab === 'dualsense' ? (
            <PS5Builder />
          ) : (
            <div className="max-w-2xl mx-auto text-center py-20 bg-gray-900 rounded-2xl border border-gray-800">
              <div className="text-6xl mb-6">🎮</div>
              <h3 className="text-3xl font-bold text-[#E0E0E0] mb-4">DualSense Edge Builder</h3>
              <p className="text-[#B0B0B0] text-lg mb-3">
                Custom builds for the DualSense Edge are coming soon.
              </p>
              <p className="text-[#B0B0B0] mb-8">
                In the meantime, reach out directly and we'll put together a custom quote for you.
              </p>
              <a
                href={`mailto:info@gamefixsolutions.com?subject=${encodeURIComponent('DualSense Edge Custom Build Quote')}&body=${encodeURIComponent('Hi, I have a DualSense Edge and I\'m interested in a custom build:\n\n')}`}
                className="inline-flex items-center gap-2 bg-[#FF1744] hover:bg-[#39FF14] text-white hover:text-black px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
              >
                <Mail className="h-5 w-5" />
                Get a Custom Quote
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Vertical Video Showcase */}
      <section className="py-20 bg-gradient-to-b from-[#0D0D0D] to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center space-x-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-6 py-2 mb-6">
              <Play className="h-4 w-4 text-[#39FF14]" />
              <span className="text-[#39FF14] font-semibold text-sm">PERFORMANCE IN ACTION</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">See Every Upgrade</h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Short clips showing exactly what each mod looks, sounds, and feels like
            </p>
          </div>

          {/* Upgrade Showcase */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-gray-800" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#39FF14]">Upgrade Showcase</span>
              <div className="h-px flex-1 bg-gray-800" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {VIDEO_UPGRADES.map(vid => (
                <VideoCard
                  key={vid.id}
                  vid={vid}
                  accentColor="#39FF14"
                  isActive={activeVideoId === vid.id}
                  isMuted={isVideoMuted}
                  onMuteToggle={() => setIsVideoMuted(m => !m)}
                  onEnded={() => advanceVideo(vid.id)}
                  onActivate={() => setActiveVideoId(vid.id)}
                />
              ))}
            </div>
          </div>

          {/* Guide Videos */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-gray-800" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#00BFFF]">Guides & Comparisons</span>
              <div className="h-px flex-1 bg-gray-800" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {VIDEO_GUIDES.map(vid => (
                <VideoCard
                  key={vid.id}
                  vid={vid}
                  accentColor="#00BFFF"
                  isActive={activeVideoId === vid.id}
                  isMuted={isVideoMuted}
                  onMuteToggle={() => setIsVideoMuted(m => !m)}
                  onEnded={() => advanceVideo(vid.id)}
                  onActivate={() => setActiveVideoId(vid.id)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mail-In Controller Service */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Wrench className="h-12 w-12 text-[#00BFFF] mx-auto mb-4" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">
              Mail-In Joystick Upgrades
            </h2>
            <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
              Send us your controller for professional GuliKit hall-effect joystick installation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800 hover:border-[#00BFFF] transition-all duration-300">
              <div className="bg-[#00BFFF]/10 rounded-xl p-4 mb-6 inline-block">
                <Package className="h-8 w-8 text-[#00BFFF]" />
              </div>

              <h3 className="text-2xl font-bold text-[#E0E0E0] mb-3">
                Standard Turnaround
              </h3>

              <p className="text-[#B0B0B0] mb-4">
                GuliKit joystick installation with thorough testing and quality assurance.
              </p>

              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-bold text-[#00BFFF]">$60</span>
                <span className="text-[#B0B0B0] ml-2">+ shipping</span>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-2 text-[#B0B0B0]">
                  <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  <span>5-7 business days</span>
                </li>
                <li className="flex items-start space-x-2 text-[#B0B0B0]">
                  <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  <span>Return shipping via USPS Priority Mail</span>
                </li>
                <li className="flex items-start space-x-2 text-[#B0B0B0]">
                  <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  <span>Full testing & calibration</span>
                </li>
                <li className="flex items-start space-x-2 text-[#B0B0B0]">
                  <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  <span>90-day warranty</span>
                </li>
              </ul>

              <a
                href="/#contact"
                className="w-full inline-flex items-center justify-center bg-[#00BFFF] hover:bg-[#39FF14] text-black px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              >
                Start Mail-In Repair
              </a>
            </div>

            <div className="bg-gray-900 rounded-2xl p-8 border-2 border-[#39FF14] hover:border-[#39FF14] transition-all duration-300 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#39FF14] text-black px-4 py-1 rounded-full text-sm font-bold">
                FASTEST
              </div>

              <div className="bg-[#39FF14]/10 rounded-xl p-4 mb-6 inline-block">
                <Zap className="h-8 w-8 text-[#39FF14]" />
              </div>

              <h3 className="text-2xl font-bold text-[#E0E0E0] mb-3">
                Expedited Turnaround
              </h3>

              <p className="text-[#B0B0B0] mb-4">
                Priority processing for gamers who can't wait. Your controller moves to the front of the queue.
              </p>

              <div className="flex items-baseline mb-6">
                <span className="text-4xl font-bold text-[#39FF14]">$95</span>
                <span className="text-[#B0B0B0] ml-2">+ shipping</span>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-2 text-[#B0B0B0]">
                  <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  <span>1-2 business days</span>
                </li>
                <li className="flex items-start space-x-2 text-[#B0B0B0]">
                  <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  <span>Expedited return shipping</span>
                </li>
                <li className="flex items-start space-x-2 text-[#B0B0B0]">
                  <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  <span>Priority queue placement</span>
                </li>
                <li className="flex items-start space-x-2 text-[#B0B0B0]">
                  <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                  <span>90-day warranty</span>
                </li>
              </ul>

              <a
                href="/#contact"
                className="w-full inline-flex items-center justify-center bg-[#39FF14] hover:bg-[#00BFFF] text-black px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              >
                Start Express Repair
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Other Controllers Section */}
      <section className="py-20 bg-[#0D0D0D]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden cursor-pointer hover:border-gray-700 transition-all duration-300"
            onClick={() => setIsOtherControllersExpanded(!isOtherControllersExpanded)}
          >
            <div className="p-8 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Gamepad2 className="h-8 w-8 text-[#00BFFF]" />
                <h2 className="text-3xl font-bold text-[#E0E0E0]">
                  Other Controllers We Support
                </h2>
              </div>
              {isOtherControllersExpanded ? (
                <ChevronUp className="h-6 w-6 text-[#B0B0B0]" />
              ) : (
                <ChevronDown className="h-6 w-6 text-[#B0B0B0]" />
              )}
            </div>

            {isOtherControllersExpanded && (
              <div className="px-8 pb-8 border-t border-gray-800 pt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="text-xl font-bold text-[#E0E0E0] mb-4">Modern Controllers</h4>
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[#E0E0E0] font-semibold">Xbox Series X|S Controllers</p>
                        <p className="text-[#B0B0B0] text-sm">Standard and Elite Series 2</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[#E0E0E0] font-semibold">Nintendo Switch Pro Controllers</p>
                        <p className="text-[#B0B0B0] text-sm">Pro and Joy-Con modifications</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[#E0E0E0] font-semibold">Steam Deck</p>
                        <p className="text-[#B0B0B0] text-sm">Joystick and button upgrades</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h4 className="text-xl font-bold text-[#E0E0E0] mb-4">Retro Controllers</h4>
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[#E0E0E0] font-semibold">Nintendo 64</p>
                        <p className="text-[#B0B0B0] text-sm">Joystick replacements and mods</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[#E0E0E0] font-semibold">GameCube Controllers</p>
                        <p className="text-[#B0B0B0] text-sm">Competitive modifications available</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Check className="h-5 w-5 text-[#39FF14] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[#E0E0E0] font-semibold">Other Retro Systems</p>
                        <p className="text-[#B0B0B0] text-sm">Contact us for compatibility</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-[#00BFFF]/10 border border-[#00BFFF]/30 rounded-lg p-6 text-center">
                  <p className="text-[#B0B0B0] mb-4">
                    Don't see your controller listed? We work with most gaming controllers. Contact us for compatibility and custom quotes.
                  </p>
                  <a
                    href="/#contact"
                    className="inline-flex items-center space-x-2 bg-[#00BFFF] hover:bg-[#39FF14] text-black px-6 py-3 rounded-lg font-semibold transition-all duration-200"
                  >
                    <Mail className="h-5 w-5" />
                    <span>Ask About Your Controller</span>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-[#B0B0B0]">
              Everything you need to know about custom controllers
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden hover:border-gray-700 transition-all duration-300"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between"
                >
                  <span className="text-[#E0E0E0] font-semibold pr-4">
                    {faq.question}
                  </span>
                  {expandedFAQ === index ? (
                    <ChevronUp className="h-5 w-5 text-[#39FF14] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#B0B0B0] flex-shrink-0" />
                  )}
                </button>

                {expandedFAQ === index && (
                  <div className="px-6 pb-6 pt-0 border-t border-gray-800">
                    <p className="text-[#B0B0B0] leading-relaxed pt-4">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0D0D0D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Gamepad2 className="h-16 w-16 text-[#39FF14] mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">
            Start Your Custom Build
          </h2>
          <p className="text-xl text-[#B0B0B0] mb-8">
            Built locally. Tuned for performance.
          </p>
          <a
            href="/#contact"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF1744] to-[#39FF14] hover:from-[#39FF14] hover:to-[#FF1744] text-white px-10 py-5 rounded-lg font-semibold text-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            <Gamepad2 className="h-6 w-6" />
            <span>Get Started Today</span>
          </a>
          <p className="text-[#B0B0B0] text-sm mt-4">
            Same-day consultations available. Contact us to discuss your build.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CustomControllersPage;
